import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { APP_VERSION } from "../constants/appVersion";

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable5 = ({ data }) => {
  const pdfRef = useRef();

  // ---- Compute Insights ----
  const peak = data.reduce(
    (max, row) => (row.all_sexes > max.all_sexes ? row : max),
    data[0],
  );

  const first = data[0].all_sexes;
  const last = data[data.length - 1].all_sexes;
  const change = last - first;
  const growthPercent = ((change / first) * 100).toFixed(1);

  // ---- Chart Config ----
  const chartData = {
    labels: data.map((item) => item.year),
    datasets: [
      {
        label: "Total Employment (All Sexes)",
        data: data.map((item) => parseInt(item.all_sexes)),
        borderColor: "#ffffbb",
        backgroundColor: "#ffffbb80",
        borderWidth: 3,
        tension: 0.4,
        pointBackgroundColor: "white",
        pointBorderColor: "#ffffbb",
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
        fill: true,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Total Workforce Trend (All Sexes)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      y: {
        beginAtZero: false,
        title: {
          display: true,
          text: "Employment Count",
          font: { size: 14, weight: "bold" },
        },
      },
      x: {
        title: {
          display: true,
          text: "Year",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

  // ---- PDF Export ----
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        const logoWidth = 20; // ideal header width
        const logoHeight = 20; // adjust ratio if needed

        pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY,
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right",
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      // ===== FINAL FOOTER SAFE AREA =====

      const footerTopY = pageHeight - 40; // start footer 40mm from bottom
      const footerContentY = pageHeight - 28; // logos row
      const footerBottomTextY = pageHeight - 10; // bottom text row

      // Top Divider Line
      pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

      // UtahData logo
      const utahWidth = 18;
      const utahHeight = 18;
      pdf.addImage(
        utahData,
        "PNG",
        20,
        footerContentY - 8,
        utahWidth,
        utahHeight,
      );

      // Built by
      pdf.setFontSize(10);
      pdf.text("Built by", 45, footerContentY);

      // BODEX logo
      const bodexWidth = 36;
      const bodexHeight = bodexWidth * 0.3;
      pdf.addImage(
        bodex,
        "PNG",
        60,
        footerContentY - 6,
        bodexWidth,
        bodexHeight,
      );

      // Using text
      pdf.text("using", 102, footerContentY);

      // KPI logo
      pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

      // Version
      pdf.text(APP_VERSION, 140, footerContentY);

      // Bottom Info Line
      pdf.setFontSize(8);
      pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        pageWidth - 10,
        footerBottomTextY,
        null,
        null,
        "right",
      );

      pdf.save("total_employment_all_sexes.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          Total Employment Overview (All Sexes)
        </h2>

        {/* Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 text-center bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Overall Growth</p>
            <p className="text-2xl font-bold text-green-800">
              +{growthPercent}%
            </p>
          </div>

          <div className="p-4 text-center bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Peak Workforce Year
            </p>
            <p className="text-2xl font-bold text-blue-800">{peak.year}</p>
          </div>

          <div className="p-4 text-center bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Latest Registered Value
            </p>
            <p className="text-2xl font-bold text-yellow-800">
              {Number(last).toLocaleString("en-US")}
            </p>
          </div>
        </div>

        {/* Table + Chart Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Year</th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Total Employment
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr
                      key={row.year}
                      className={`${row.year === peak.year ? "bg-green-100 font-bold" : "hover:bg-gray-100"}`}
                    >
                      <td className="p-2 text-left">{row.year}</td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {Number(row.all_sexes).toLocaleString("en-US")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* PDF Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          Download as PDF
          <img src={pdf} className="w-[1.5rem]" alt="Download icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable5;

